import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronRight, Gamepad2, Globe, Swords, Trophy, User, Users, Wifi, Zap } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { GameCanvas } from '../components/GameCanvas'
import { OnlineMatchCanvas } from '../components/OnlineMatchCanvas'
import { MultiplayerLobby } from '../components/MultiplayerLobby'
import { FlameKnight, ShadowNinja, CyberMech, ForestDruid, StormMonk } from '../components/characters'
import { NeonCity, VolcanicArena, AncientTemple, SpaceStation, Battlefield } from '../components/stages'

const characterComponents = {
  flameKnight: FlameKnight,
  shadowNinja: ShadowNinja,
  cyberMech: CyberMech,
  forestDruid: ForestDruid,
  stormMonk: StormMonk,
  brawler: FlameKnight,
  ranger: ShadowNinja,
  striker: ForestDruid,
  tank: CyberMech,
}

const characterColors = {
  flameKnight: '#ef4444',
  shadowNinja: '#a855f7',
  cyberMech: '#06b6d4',
  forestDruid: '#84cc16',
  stormMonk: '#eab308',
  brawler: '#ef4444',
  ranger: '#a855f7',
  striker: '#84cc16',
  tank: '#06b6d4',
}

const stageComponents = {
  neonPier: NeonCity,
  reactorCore: VolcanicArena,
  skySpire: AncientTemple,
  ironRing: SpaceStation,
  battlefield: Battlefield,
}

export function Play() {
  const { state, dispatch, stagePresets, tabs } = useApp()
  const [activeMatch, setActiveMatch] = useState(null)
  const [multiplayerMode, setMultiplayerMode] = useState(null) // 'host', 'join', 'quick'
  const currentUser = state.users.find(u => u.id === state.currentUserId)

  if (!currentUser) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <h2 className="text-2xl font-bold mb-4">Sign in to Play</h2>
        <p className="text-gray-400 mb-6">Create an account or sign in to start fighting</p>
        <Link to="/" className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg">
          Go to Sign In
        </Link>
      </div>
    )
  }

  const selectedCharacter = state.characters.find(c => c.id === state.selectedCharacterId)
  const selectedCpu = state.characters.find(c => c.id === state.selectedCpuCharacterId)
  const stagePreset = stagePresets[state.currentStageId] || stagePresets.neonPier
  const StagePreview = stageComponents[stagePreset.id] || NeonCity
  const roster = state.characters
  const playerFighter = selectedCharacter || roster[0]
  const cpuFighter = selectedCpu || roster.find(c => c.id !== playerFighter?.id) || roster[1] || roster[0]

  const getCharacterView = (character) => {
    const archetype = character?.archetype || 'flameKnight'
    return {
      Component: characterComponents[archetype] || FlameKnight,
      color: characterColors[archetype] || '#06b6d4',
      name: character?.name || 'Select Fighter',
    }
  }

  const playerView = getCharacterView(playerFighter)
  const cpuView = getCharacterView(cpuFighter)
  const PlayerPreview = playerView.Component
  const CpuPreview = cpuView.Component

  const handleStartMatch = (mode) => {
    setActiveMatch(mode)
  }

  const handleMatchEnd = (result) => {
    dispatch({
      type: 'ADD_MATCH',
      payload: {
        id: crypto.randomUUID(),
        winner: result.winner,
        mode: result.mode,
        date: Date.now(),
        opponent: result.mode === 'cpu' ? selectedCpu?.name : 'Player 2',
      }
    })
  }

  const handleExitMatch = () => {
    setActiveMatch(null)
  }

  // Show online match canvas when in online mode (check BEFORE multiplayer lobby)
  if (state.playMode === 'online' && state.currentMatch && selectedCharacter) {
    const playerSlot = state.currentMatch.player1Id === currentUser.id ? 'p1' : 'p2'
    return (
      <div className="space-y-4 p-6 lg:p-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Online Match</h1>
            <p className="text-gray-400 text-sm">
              {selectedCharacter.name} vs Online Opponent
            </p>
          </div>
          <button
            onClick={() => {
              dispatch({ type: 'SET_PLAY_MODE', payload: null })
              dispatch({ type: 'SET_MATCH', payload: null })
              setMultiplayerMode(null)
            }}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all text-sm"
          >
            Exit Match
          </button>
        </div>

        <OnlineMatchCanvas
          match={state.currentMatch}
          playerCharacter={selectedCharacter}
          playerSlot={playerSlot}
          stagePresets={stagePresets}
          onMatchEnd={handleMatchEnd}
          isActive={true}
        />
      </div>
    )
  }

  // Show game canvas when local match is active
  if (activeMatch && selectedCharacter) {
    return (
      <div className="space-y-4 p-6 lg:p-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">
              {activeMatch === 'cpu' ? 'vs CPU' : 'Local Multiplayer'}
            </h1>
            <p className="text-gray-400 text-sm">
              {selectedCharacter.name} {activeMatch === 'cpu' ? `vs ${selectedCpu?.name || 'CPU'}` : 'vs Player 2'}
            </p>
          </div>
          <button
            onClick={handleExitMatch}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all text-sm"
          >
            Exit Match
          </button>
        </div>

        <GameCanvas
          playerCharacter={selectedCharacter}
          cpuCharacter={selectedCpu || selectedCharacter}
          stagePreset={stagePreset}
          mode={activeMatch}
          onMatchEnd={handleMatchEnd}
          isActive={true}
        />
      </div>
    )
  }

  // Show multiplayer lobby
  if (multiplayerMode) {
    return (
      <div className="p-6 lg:p-10">
        <MultiplayerLobby 
          mode={multiplayerMode} 
          onExit={() => setMultiplayerMode(null)} 
        />
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-y-auto bg-gray-950 p-6 text-white lg:overflow-hidden lg:p-10">
      <div className="absolute inset-0 z-0 pointer-events-none">
        <StagePreview className="h-full w-full scale-105 object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/95 to-gray-900/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-gray-950/40" />
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-[1600px] flex-col gap-6 lg:min-h-[calc(100vh-5rem)]">
        <header className="flex w-full shrink-0 flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div className="flex flex-col">
            <h1 className="text-4xl font-black uppercase italic tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400 drop-shadow-lg md:text-5xl">
              Cache Smash
            </h1>
            <p className="mt-1 text-sm font-bold uppercase tracking-widest text-cyan-400">
              A browser-native platform fighter
            </p>
          </div>

          <div className="flex flex-wrap gap-3 text-xs font-bold uppercase tracking-wider">
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg">
              <User size={14} className="text-cyan-400" />
              <span>{currentUser.name}</span>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg sm:flex">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <span>Online</span>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg md:flex">
              <Users size={14} className="text-gray-400" />
              <span>{roster.length} Fighters</span>
            </div>
            <div className="hidden items-center gap-2 rounded-full border border-white/10 bg-gray-900/80 px-4 py-2 shadow-lg lg:flex">
              <Trophy size={14} className="text-yellow-400" />
              <span>{state.tournaments.length} Tournaments</span>
            </div>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 lg:flex-row lg:gap-10">
        <motion.nav
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
          }}
          className="flex w-full shrink-0 flex-row gap-2 overflow-x-auto pb-2 lg:w-64 lg:flex-col lg:overflow-visible lg:pb-0 lg:pt-12"
        >
          {tabs.map(([id, label]) => {
            const isActive = id === 'play'
            const to = id === 'home' ? '/' : `/${id}`

            return (
              <motion.div
                key={id}
                variants={{
                  hidden: { opacity: 0, x: -20 },
                  visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } },
                }}
              >
                <Link
                  to={to}
                  className={`group relative flex shrink-0 items-center px-4 py-3 text-left transition-all duration-300 lg:w-full ${isActive ? 'scale-105 text-white' : 'text-gray-500 hover:text-gray-200'}`}
                >
                  <div className={`absolute left-0 top-0 h-1 w-full origin-top bg-cyan-500 transition-all duration-300 lg:h-full lg:w-1 lg:origin-left ${isActive ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} />
                  <div className="absolute inset-0 hidden bg-gradient-to-r from-white/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:block" />
                  <span className="relative z-10 whitespace-nowrap text-xl font-black uppercase italic tracking-wider lg:text-2xl">
                    {label}
                  </span>
                  {isActive && <ChevronRight className="relative z-10 ml-auto hidden text-cyan-500 lg:block" size={24} />}
                </Link>
              </motion.div>
            )
          })}
        </motion.nav>

        <main className="flex min-w-0 flex-1 flex-col gap-4">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center"
      >
        <div className="shrink-0">
          <h1 className="text-3xl font-black uppercase italic tracking-tight drop-shadow-lg">Play</h1>
        </div>

        <Link
          to="/stages"
          className="group relative h-14 flex-1 overflow-hidden rounded-lg border border-white/10 transition-colors hover:border-cyan-500/50"
        >
          <div className="absolute inset-0">
            <StagePreview className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-950/90 via-gray-950/70 to-gray-950/40" />
          </div>
          <div className="relative z-10 flex h-full items-center justify-between px-4">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">Stage:</span>
              <span className="text-sm font-black uppercase">{stagePreset.name}</span>
            </div>
            <ChevronRight className="text-gray-500 transition-colors group-hover:text-cyan-400" size={18} />
          </div>
        </Link>

        <div className="flex shrink-0 items-center gap-2 rounded-full border border-green-500/30 bg-green-500/20 px-3 py-1.5">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-green-400">Online</span>
        </div>
      </motion.div>

      <div className="grid flex-1 gap-4 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="flex flex-col rounded-xl border border-white/10 bg-gray-900/60 p-4 backdrop-blur-sm transition-colors hover:border-cyan-500/30"
        >
          <div className="mb-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Swords className="text-cyan-500/50" size={22} />
              <h2 className="text-lg font-black uppercase italic">Fight CPU</h2>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">Single Player</span>
          </div>

          <div className="flex flex-1 gap-3">
            <div className="flex flex-1 flex-col items-center rounded-lg border border-white/5 bg-black/40 p-3 text-center">
              <span className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">You</span>
              <div
                className="mb-2 flex h-24 w-20 items-center justify-center overflow-hidden rounded-lg border-2 bg-black/60"
                style={{ borderColor: playerView.color }}
              >
                <PlayerPreview className="mt-2 h-24 w-16" />
              </div>
              <span className="mb-3 w-full truncate text-xs font-black uppercase" style={{ color: playerView.color }}>
                {playerView.name}
              </span>
              <div className="flex flex-wrap justify-center gap-1">
                {roster.map((fighter) => {
                  const FighterIcon = characterComponents[fighter.archetype] || FlameKnight
                  return (
                    <button
                      key={fighter.id}
                      onClick={() => dispatch({ type: 'SELECT_CHARACTER', payload: fighter.id })}
                      title={fighter.name}
                      className={`flex h-8 w-8 items-center justify-center overflow-hidden rounded border transition-all ${state.selectedCharacterId === fighter.id ? 'scale-110 border-white' : 'border-white/10 opacity-45 hover:opacity-100'}`}
                    >
                      <FighterIcon className="mt-1 h-8 w-6" />
                    </button>
                  )
                })}
              </div>
              {!playerFighter && (
                <Link to="/characters" className="mt-3 text-xs font-bold uppercase tracking-widest text-cyan-400 hover:text-cyan-300">
                  Create Fighter
                </Link>
              )}
            </div>

            <div className="flex shrink-0 items-center justify-center">
              <span className="text-2xl font-black italic text-gray-600">VS</span>
            </div>

            <div className="flex flex-1 flex-col items-center rounded-lg border border-white/5 bg-black/40 p-3 text-center">
              <span className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">CPU</span>
              <div
                className="mb-2 flex h-24 w-20 items-center justify-center overflow-hidden rounded-lg border-2 bg-black/60"
                style={{ borderColor: cpuView.color }}
              >
                <CpuPreview className="mt-2 h-24 w-16" />
              </div>
              <span className="mb-3 w-full truncate text-xs font-black uppercase" style={{ color: cpuView.color }}>
                {cpuView.name}
              </span>
              <div className="flex flex-wrap justify-center gap-1">
                {roster.map((fighter) => {
                  const FighterIcon = characterComponents[fighter.archetype] || FlameKnight
                  return (
                    <button
                      key={fighter.id}
                      onClick={() => dispatch({ type: 'SELECT_CPU_CHARACTER', payload: fighter.id })}
                      title={fighter.name}
                      className={`flex h-8 w-8 items-center justify-center overflow-hidden rounded border transition-all ${state.selectedCpuCharacterId === fighter.id ? 'scale-110 border-white' : 'border-white/10 opacity-45 hover:opacity-100'}`}
                    >
                      <FighterIcon className="mt-1 h-8 w-6" />
                    </button>
                  )
                })}
              </div>
              {!cpuFighter && (
                <Link to="/characters" className="mt-3 text-xs font-bold uppercase tracking-widest text-pink-400 hover:text-pink-300">
                  Create CPU
                </Link>
              )}
            </div>
          </div>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleStartMatch('cpu')}
              disabled={!selectedCharacter || !selectedCpu}
              className="rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 py-3 text-base font-black uppercase italic tracking-wider text-black shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-shadow hover:shadow-[0_0_30px_rgba(6,182,212,0.6)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Start Match
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleStartMatch('local')}
              disabled={!selectedCharacter}
              className="rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 py-3 text-base font-black uppercase italic tracking-wider text-white shadow-[0_0_20px_rgba(168,85,247,0.35)] transition-shadow hover:shadow-[0_0_30px_rgba(168,85,247,0.55)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Local 1v1
            </motion.button>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 }}
          className="flex flex-col rounded-xl border border-white/10 bg-gray-900/60 p-4 backdrop-blur-sm transition-colors hover:border-emerald-500/30"
        >
          <div className="mb-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Globe className="text-emerald-500/50" size={22} />
              <h2 className="text-lg font-black uppercase italic">Go Online</h2>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400">Multiplayer</span>
          </div>

          <div className="grid flex-1 gap-3">
            {[
              { label: 'Host Lobby', desc: 'Create a private room and invite friends', icon: Wifi, action: () => setMultiplayerMode('host'), disabled: false },
              { label: 'Join Lobby', desc: 'Enter an invite code to join a match', icon: Gamepad2, action: () => setMultiplayerMode('join'), disabled: false },
              { label: 'Quick Match', desc: 'Matchmaking queue coming soon', icon: Zap, action: null, disabled: true },
            ].map((item) => (
              <motion.button
                key={item.label}
                whileHover={item.disabled ? undefined : { scale: 1.01 }}
                whileTap={item.disabled ? undefined : { scale: 0.99 }}
                onClick={item.action || undefined}
                disabled={item.disabled}
                className="group flex items-center gap-4 rounded-lg border border-white/5 bg-black/40 p-4 text-left transition-colors hover:border-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-white/5"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 transition-colors group-hover:bg-emerald-500/20">
                  <item.icon className="text-emerald-500/60 transition-colors group-hover:text-emerald-400" size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-black uppercase">{item.label}</h3>
                  <p className="mt-0.5 text-[11px] text-gray-500">{item.desc}</p>
                </div>
                <ChevronRight className="ml-auto shrink-0 text-gray-700 transition-colors group-hover:text-emerald-400" size={18} />
              </motion.button>
            ))}
          </div>

          <div className="mt-3 rounded-lg border border-white/5 bg-black/30 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-black uppercase italic text-white">
              <Users size={16} className="text-emerald-400" />
              Controls
            </div>
            <p className="text-xs text-gray-500">Online players use WASD movement, F attack, G special, and Shift dash.</p>
            <p className="mt-1 text-xs text-gray-500">Local Player 2 uses Arrow keys, / attack, . special, and Right Shift dash.</p>
          </div>
        </motion.section>
      </div>
        </main>
        </div>
      </div>
    </div>
  )
}
